import React, { useState, useEffect, useRef } from 'react';
import 'bulma/css/bulma.min.css';

import { truthyJoin } from '../../utils.tsx';
import { ParameterChoice } from '../../scanTypes.ts';
import { CellFunctions, CellActivationStatus, CellValidationStatus, CellHelpStatus, CellHelpMessage, CellValidationResult, CellNavigationDirection, CellSubcomponentFunctions } from './cellDto.ts';
import { CellTextfield } from './cellTextfield.tsx';
import { CellModal } from './cellModal.tsx';
import { CellAutocomplete } from './cellAutocomplete.tsx';
import { CellCheckbox, CellCheckboxValue } from './cellCheckbox.tsx';


// Settings passed in with the React component
interface EditableCellParameters {
  x: number;
  y: number;
  activationStatus: CellActivationStatus;
  value: any;
  // Used if defined, otherwise value is used for display (and assumed to be a string).
  displayValue?: string;
  description?: string;
  // If this is set, a read-only alert will be appended to the description,
  // and all edits to the cell will revert to the previous value.
  isReadOnly?: boolean;
  isUnused?: boolean;
  isWithinSelection?: boolean;
  // This parameter is used to indicate that a CellAutocomplete should be
  // used instead of a CellTextfield.
  choices?: ParameterChoice[];
  // If set, the cell will present as a checkbox, and the value will be interpreted as a boolean.
  isBoolean?: boolean;
  useModal?: boolean;
  modalTitle?: string;
  customModalContent?: (value: any, onValueChange: (input: any) => void) => React.JSX.Element;
  cellFunctions: CellFunctions;
}


// Given a DOM node, walk up the tree looking for a node of type "div"
// with class "value" assigned to it, and return the pixel width of that element,
// or 0 if a matching node can't be found.
function findCellSize(node: HTMLElement): { h: number, w: number } | null {
  var n = node.nodeName || "";
  n = n.trim().toLowerCase();
  const p = node.parentNode as HTMLElement | null;
  if (n != "td") {
      if (!p) { return null } else { return findCellSize(p) }
  } else {
    const c = node.classList;
    if (c.contains("editablecell")) {
        const rect = node.getBoundingClientRect();
        return { w: rect.width, h: rect.height };
    } else if (!p) {
      return null
    } else {
      return findCellSize(p)
    }
  }
}


const EditableTableCell: React.FC<EditableCellParameters> = (props) => {

  const [justActivated, setJustActivated] = useState<CellActivationStatus>(CellActivationStatus.Inactive);
  const [lastKnownHeight, setLastKnownHeight] = useState<string>("unset");
  const [lastKnownWidth, setLastKnownWidth] = useState<string>("unset");
  const [helpMessages, setHelpMessages] = useState<React.JSX.Element[]>([]);

  const valueRef = useRef<HTMLDivElement>(null);


  function validate(value: string): CellValidationResult {
    return props.cellFunctions.validate(props.x, props.y, value);
  }


  function save(inputValue: string): CellValidationResult {
    var value = inputValue;
    if (typeof value === "string") {
      // Filtering out strange versions of space, for sanity.
      value = inputValue.replace(/\u202F|\u00A0/g, ' ');
      // No multi-line inputs are allowed for table cells.
      value = value.replace(/\n/g, ' ');
      value = value.trim();
    }
    // If the save is successful we expect props.value to change
    // which will update the control.
    return props.cellFunctions.save(props.x, props.y, value);
  }


  function close(): void {
    return props.cellFunctions.close(props.x, props.y);
  }


  function setHelp(help: CellHelpMessage[]) {
    const helpElements = help.map((m, i) => {
      if (m.status == CellHelpStatus.Info) {
        return (<p key={i} className="help is-info">{ m.message }</p>);
      } else if (m.status == CellHelpStatus.Danger) {
        return (<p key={i} className="help is-danger">{ m.message }</p>);
      } else {
        return (<p key={i} className="help">{ m.message }</p>);
      }
    });
    if (props.isReadOnly) {
      helpElements.push((<p key="ro" className="help is-warning">This value is read-only.</p>));
    }
    setHelpMessages(helpElements);
  }


  // This handles keyboard-based navigation for the table cell.
  // It's meant to be called after the specific input component does its own business.
  // Returns true if the outcome is a move to another cell, false otherwise.
  function testKeyForMovement(event: React.KeyboardEvent<HTMLInputElement| HTMLTextAreaElement>, useArrows: boolean): boolean {
    var didMove: CellValidationStatus = CellValidationStatus.Failure;

    switch (event.key) {
      case "Enter":
        didMove = props.cellFunctions.move(props.x, props.y, CellNavigationDirection.Down);
        break;
      case "ArrowDown":
        if (useArrows) {
          didMove = props.cellFunctions.move(props.x, props.y, CellNavigationDirection.Down);
        }
        break;
      case "ArrowUp":
        if (useArrows) {
          didMove = props.cellFunctions.move(props.x, props.y, CellNavigationDirection.Up);
        }
        break;
      case "Tab":
        if (event.shiftKey) {
          didMove = props.cellFunctions.move(props.x, props.y, CellNavigationDirection.Left);
        } else {
          didMove = props.cellFunctions.move(props.x, props.y, CellNavigationDirection.Right);
        }
        break;
      case "Escape":
        close();
        didMove = CellValidationStatus.Success;
        break;
      }
    if (didMove == CellValidationStatus.Success) {
      event.preventDefault();
      return true;
    }
    return false;
  }


  useEffect(() => {
    // When the state goes from inactive to active,
    // we measure the size of the cell and set the input element to match
    // before revealing it.
    if (props.activationStatus != CellActivationStatus.Inactive) {
      var w = "unset";
      var h = "unset";
      if (valueRef.current) {
        const d = findCellSize( valueRef.current as HTMLElement);
        if (d !== null) {
          // 6 pixels to account for the padding that a cell in non-editing mode gets
          const height = Math.max(d.h - 6, 20)
          // 10 pixels accounts for the extra padding of the input element
          // relative to the table cell when it's showing the value element.
          const width = Math.max(d.w - 10, 20)
          h = `${height}px`;
          w = `${width}px`;
        }
      }
      setLastKnownHeight(h);
      setLastKnownWidth(w);
    }
    // This state value is used to create a delayed reaction, where the
    // input element is revealed _after_ the size of the table cell is measured.
    // Otherwise we would be attempting to measure a table cell that has already
    // revealed the input field, and the value element would have width of 0
    // since it's hidden by "display: none".
    setJustActivated(props.activationStatus);
    return () => {};
  }, [props.activationStatus]);


  // Now we have all the functions we need 
  const cellSubcomponentFunctions: CellSubcomponentFunctions = {
    validate: validate,
    save: save,
    close: close,
    setHelp: setHelp,
    testKeyForMovement: testKeyForMovement
  }

  const divClass = truthyJoin((justActivated != CellActivationStatus.Inactive) && "editing");
  const helpClass = truthyJoin("notify", (helpMessages.length > 0) && "disclosed");

  return (
      <div className={ divClass }>
        <div className="value" ref={ valueRef }>{
          props.isUnused ?
            (<span>&nbsp;</span>)
          : props.isBoolean ?
            (<CellCheckboxValue value={ props.value } />)
          : (props.displayValue !== undefined) ?
            props.displayValue
          : props.value
        }</div>
        <div className="editingarea">{
          props.isBoolean ?
            (<CellCheckbox
              activationStatus={ justActivated }
              value={ props.value }
              description={ props.description }
              isReadOnly={ props.isReadOnly }
              cellFunctions={ cellSubcomponentFunctions } />)
          : props.choices ?
            (<CellAutocomplete
              activationStatus={ justActivated }
              value={ props.value }
              choices={ props.choices }
              description={ props.description }
              isReadOnly={ props.isReadOnly }
              lastKnownWidth={ lastKnownWidth }
              cellFunctions={ cellSubcomponentFunctions } />)
          : props.useModal ?
            (<CellModal
              activationStatus={ justActivated }
              value={ props.value }
              description={ props.description }
              isReadOnly={ props.isReadOnly }
              modalTitle={ props.modalTitle }
              customModalContent={ props.customModalContent }
              cellFunctions={ cellSubcomponentFunctions } />)
          : (<CellTextfield
              activationStatus={ justActivated }
              value={ props.value }
              description={ props.description }
              isReadOnly={ props.isReadOnly }
              lastKnownHeight={ lastKnownHeight }
              lastKnownWidth={ lastKnownWidth }
              cellFunctions={ cellSubcomponentFunctions } />)
          }<div className={ helpClass }> 
            <div className="notify-content">
              { helpMessages }
            </div>
          </div>
        </div>
      </div>
  );
}


export { EditableTableCell }
export type { EditableCellParameters }
